/**
 * SEO Canonical URL utilities
 * Ensures proper canonical URLs across all SEO pages
 */

const BASE_URL = 'https://vroum.gr';

/**
 * Generate canonical URL for different page types
 */
export const getCanonicalUrl = (pageType, params = {}) => {
  const { city, service, brand, code, symptom, slug, garageId, category } = params;
  
  switch (pageType) {
    case 'home':
      return BASE_URL;
    
    case 'city':
      return `${BASE_URL}/synergeia/${city}`;
    
    case 'service':
      return `${BASE_URL}/ypiresies/${service}`;
    
    case 'service_city':
      return `${BASE_URL}/ypiresies/${service}/${city}`;
    
    case 'cost':
      return `${BASE_URL}/kostos/${service}`;
    
    case 'obd':
      return `${BASE_URL}/obd/${code?.toLowerCase()}`;
    
    case 'symptom':
      return `${BASE_URL}/symptoms/${symptom}`;
    
    case 'brand':
      return `${BASE_URL}/markes/${brand}/synergeia`;
    
    case 'brand_city':
      return `${BASE_URL}/synergeia/${city}/${brand}`;
    
    case 'category':
      return `${BASE_URL}/kathgories/${category}`;
    
    case 'garage':
      return `${BASE_URL}/garage/${garageId}`;
    
    case 'blog':
      return `${BASE_URL}/blog/${slug}`;
    
    case 'blog_list':
      return `${BASE_URL}/blog`;
    
    default:
      return BASE_URL;
  }
};

/**
 * Get robots meta directive based on page characteristics
 */
export const getRobotsMeta = (options = {}) => {
  const { 
    isSearchPage = false,
    hasFilters = false,
    isPaginated = false,
    isPrivate = false,
    isThinContent = false
  } = options;
  
  // Noindex rules
  if (isPrivate || isSearchPage || (hasFilters && isPaginated) || isThinContent) {
    return 'noindex, follow';
  }
  
  // Default: index and follow
  return 'index, follow';
};

/**
 * Private/auth paths that should never be indexed
 */
export const NOINDEX_PATHS = [
  '/login',
  '/auth',
  '/admin',
  '/dashboard',
  '/my-garage',
  '/my-cars',
  '/my-service-book',
  '/checkout',
  '/advertiser',
  '/claim/',
  '/booking/success',
  '/booking/cancel',
  '/compare',
  '/favorites',
  '/register-garage',
  '/eggrafi-synergeiou'
];

/**
 * Check if current path should be noindexed
 */
export const shouldNoindex = (pathname) => {
  // Check exact matches
  for (const path of NOINDEX_PATHS) {
    if (pathname === path || pathname.startsWith(path)) {
      return true;
    }
  }
  
  // Check search/filter patterns
  if (pathname === '/search' || pathname.startsWith('/search?')) {
    return true;
  }
  
  return false;
};

/**
 * Generate hreflang tags (for future multi-language support)
 */
export const getHreflangTags = (canonicalUrl) => {
  return [
    { rel: 'alternate', hreflang: 'el', href: canonicalUrl },
    { rel: 'alternate', hreflang: 'x-default', href: canonicalUrl }
  ];
};

/**
 * Generate Open Graph meta tags
 */
export const getOpenGraphTags = (options = {}) => {
  const {
    title,
    description,
    url,
    image = `${BASE_URL}/og-image.png`,
    type = 'website',
    siteName = 'VROUM'
  } = options;
  
  return {
    'og:title': title,
    'og:description': description,
    'og:url': url,
    'og:image': image,
    'og:type': type,
    'og:site_name': siteName,
    'og:locale': 'el_GR'
  };
};

/**
 * Generate Twitter Card meta tags
 */
export const getTwitterTags = (options = {}) => {
  const {
    title,
    description,
    image = `${BASE_URL}/twitter-card.png`,
    card = 'summary_large_image'
  } = options;
  
  return {
    'twitter:card': card,
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': image
  };
};

/**
 * Safely coerce a garage's services field to an array.
 * Some scraped garages have services as a string instead of an array — calling
 * .slice().join() on a string returns garbage and crashes the page.
 * Always use this before .slice() / .join() on garage.services.
 */
export const toServiceArray = (services) => {
  if (!services) return [];
  if (Array.isArray(services)) return services;
  if (typeof services === 'string') return services.split(',').map(s => s.trim()).filter(Boolean);
  return [];
};

export default {
  getCanonicalUrl,
  getRobotsMeta,
  shouldNoindex,
  getHreflangTags,
  getOpenGraphTags,
  getTwitterTags,
  NOINDEX_PATHS,
  BASE_URL
};
